import { useMemo, useState } from 'react';
import { Check, ChevronRight, Flag, Heart, LocateFixed, RefreshCw } from 'lucide-react';
import type { Museum } from '../data/types';
import { useT } from '../hooks/useT';
import type { LocatieStatus } from '../hooks/useLocatie';
import { afstandKm, isBezocht, type StatusMap } from '../utils/museum';
import { chip } from '../utils/ui';
import { CATEGORIE_ICOON } from './categorie';

interface BuurtSchermProps {
  musea: Museum[];
  status: StatusMap;
  locatie: { status: LocatieStatus; pos: { lat: number; lng: number } | null };
  onVraagLocatie: () => void;
  onKies: (m: Museum) => void;
}

const MAX = 20;

/** Dichtstbijzijnde musea vanaf je huidige locatie; standaard alleen wat je nog niet gezien hebt. */
export function BuurtScherm({ musea, status, locatie, onVraagLocatie, onKies }: BuurtSchermProps) {
  const { t, tf } = useT();
  const [ookBezocht, setOokBezocht] = useState(false);
  const pos = locatie.pos;

  const lijst = useMemo(() => {
    if (!pos) return [];
    return musea
      .filter((m) => ookBezocht || !isBezocht(status[m.id]))
      .map((m) => ({ m, km: afstandKm(pos, m) }))
      .sort((a, b) => a.km - b.km)
      .slice(0, MAX);
  }, [musea, status, pos, ookBezocht]);

  return (
    <section className="space-y-4" aria-labelledby="buurt-titel">
      <div className="space-y-2">
        <h2 id="buurt-titel" className="text-2xl font-bold">{t('buurtTitel')}</h2>
        <p className="text-slate-600 dark:text-slate-400">{t('buurtUitleg')}</p>
      </div>

      {!pos && (
        <div className="space-y-3">
          <button
            onClick={onVraagLocatie}
            disabled={locatie.status === 'bezig'}
            className="flex min-h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-indigo-700 px-5 font-semibold text-white transition-all duration-150 active:scale-95 disabled:opacity-70"
          >
            <LocateFixed size={18} aria-hidden /> {locatie.status === 'bezig' ? t('buurtBezig') : t('buurtKnop')}
          </button>
          {locatie.status === 'fout' && (
            <p role="alert" className="rounded-2xl bg-amber-100/90 p-4 text-sm text-amber-950 dark:bg-amber-950/60 dark:text-amber-100">
              {t('buurtGeweigerd')}
            </p>
          )}
        </div>
      )}

      {pos && (
        <>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setOokBezocht((v) => !v)} aria-pressed={ookBezocht} className={chip(ookBezocht)}>
              <Check size={16} aria-hidden /> {t('buurtOokBezocht')}
            </button>
            <button onClick={onVraagLocatie} className={chip(false)} disabled={locatie.status === 'bezig'}>
              <RefreshCw size={16} aria-hidden className={locatie.status === 'bezig' ? 'animate-spin' : ''} /> {t('buurtVernieuw')}
            </button>
          </div>

          {lijst.length === 0 ? (
            <p className="rounded-3xl bg-emerald-100/90 p-5 text-emerald-950 dark:bg-emerald-950/60 dark:text-emerald-100">{t('buurtLeeg')}</p>
          ) : (
            <ol className="divide-y divide-black/5 overflow-hidden rounded-3xl bg-slate-50/80 backdrop-blur-md dark:divide-white/10 dark:bg-slate-900/70">
              {lijst.map(({ m, km }) => {
                const s = status[m.id];
                const Icon = CATEGORIE_ICOON[m.categorie];
                return (
                  <li key={m.id}>
                    <button
                      onClick={() => onKies(m)}
                      className="flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left transition-all duration-150 hover:bg-black/5 active:scale-[0.99] dark:hover:bg-white/5"
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-indigo-100 text-indigo-900 dark:bg-indigo-950/70 dark:text-indigo-200">
                        <Icon size={18} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 font-semibold">
                          <span className="truncate">{m.naam}</span>
                          {isBezocht(s) && <Check size={16} className="shrink-0 text-emerald-700 dark:text-emerald-400" aria-label={t('bezocht')} />}
                          {s?.w && <Flag size={16} className="shrink-0 text-amber-700 dark:text-amber-400" aria-label={t('wens')} />}
                          {s?.f && <Heart size={16} className="shrink-0 text-rose-700 dark:text-rose-400" aria-label={t('fav')} />}
                        </span>
                        <span className="block text-sm text-slate-600 dark:text-slate-400">{m.plaats}</span>
                      </span>
                      <span className="whitespace-nowrap text-sm font-semibold tabular-nums">
                        {tf('afstand', { km: km < 10 ? km.toFixed(1) : Math.round(km) })}
                      </span>
                      <ChevronRight size={18} className="shrink-0 text-slate-600 dark:text-slate-400" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </section>
  );
}
